import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  RocketLaunch as ActivateIcon,
  PauseCircle as DeactivateIcon,
  AutoAwesome as AIIcon,
  CloudUpload as UploadIcon,
  ExpandMore as ExpandMoreIcon,
  PhoneIphone as IOSIcon,
  Android as AndroidIcon,
  Stars as SparklesIcon,
} from "@mui/icons-material";
import { useAuth } from "../hooks/useAuth";
import CrossPromotionService from "../services/CrossPromotionService";
import AIService from "../services/AIService";
import AppService from "../services/AppService";
import type { App } from "../types/app";
import {
  CrossPromotionCampaign,
  CrossPromotionDesignStyle,
  CreateCrossPromotionCampaignDTO,
  CrossPromotionLocalizedCopy,
} from "../types/crossPromotion";

const AVAILABLE_LANGUAGES = [
  {
    code: "en",
    name: "English",
    flag: "🇬🇧",
    countryCode: "GB",
    speakers: "1.5B",
  },
  {
    code: "zh",
    name: "Chinese (Simplified)",
    flag: "🇨🇳",
    countryCode: "CN",
    speakers: "1.1B",
  },
  {
    code: "hi",
    name: "Hindi",
    flag: "🇮🇳",
    countryCode: "IN",
    speakers: "602M",
  },
  {
    code: "es",
    name: "Spanish",
    flag: "🇪🇸",
    countryCode: "ES",
    speakers: "559M",
  },
  {
    code: "fr",
    name: "French",
    flag: "🇫🇷",
    countryCode: "FR",
    speakers: "280M",
  },
  {
    code: "ar",
    name: "Arabic",
    flag: "🇸🇦",
    countryCode: "SA",
    speakers: "274M",
  },
  {
    code: "bn",
    name: "Bengali",
    flag: "🇧🇩",
    countryCode: "BD",
    speakers: "272M",
  },
  {
    code: "pt",
    name: "Portuguese",
    flag: "🇵🇹",
    countryCode: "PT",
    speakers: "264M",
  },
  {
    code: "ru",
    name: "Russian",
    flag: "🇷🇺",
    countryCode: "RU",
    speakers: "258M",
  },
  {
    code: "ja",
    name: "Japanese",
    flag: "🇯🇵",
    countryCode: "JP",
    speakers: "125M",
  },
  {
    code: "de",
    name: "German",
    flag: "🇩🇪",
    countryCode: "DE",
    speakers: "134M",
  },
  {
    code: "ko",
    name: "Korean",
    flag: "🇰🇷",
    countryCode: "KR",
    speakers: "81M",
  },
  {
    code: "it",
    name: "Italian",
    flag: "🇮🇹",
    countryCode: "IT",
    speakers: "68M",
  },
  {
    code: "tr",
    name: "Turkish",
    flag: "🇹🇷",
    countryCode: "TR",
    speakers: "88M",
  },
  {
    code: "vi",
    name: "Vietnamese",
    flag: "🇻🇳",
    countryCode: "VN",
    speakers: "85M",
  },
] as const;

const DESIGN_STYLE_OPTIONS: Array<{
  value: CrossPromotionDesignStyle;
  label: string;
}> = [
  { value: "screenshot_classic", label: "Screenshot Classic" },
  { value: "screenshot_bold", label: "Screenshot Bold" },
  { value: "screenshot_minimal", label: "Screenshot Minimal" },
];

const COPY_LIMITS = {
  headline: 52,
  subheading: 110,
  cta: 18,
};

type PreviewPlatform = "ios" | "android";

type CampaignPreviewPayload = {
  headline: string;
  subheading: string;
  cta: string;
  adImageUrl: string;
  campaignDesignStyle: CrossPromotionDesignStyle;
  storeLink: string;
};

const PreviewImageTile: React.FC<{ src: string; size: number }> = ({ src, size }) =>
  src ? (
    <Box
      component="img"
      src={src}
      alt="Campaign creative"
      sx={{
        width: size,
        height: size,
        borderRadius: 2,
        objectFit: "cover",
        border: "1px solid",
        borderColor: "rgba(148, 163, 184, 0.35)",
      }}
    />
  ) : (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: 2,
        border: "1px dashed",
        borderColor: "rgba(148, 163, 184, 0.6)",
        color: "text.secondary",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 12,
        textAlign: "center",
        px: 1,
      }}
    >
      Image placeholder
    </Box>
  );

const textClampSx = (lines: number) => ({
  overflow: "hidden",
  display: "-webkit-box",
  WebkitLineClamp: lines,
  WebkitBoxOrient: "vertical" as const,
});

const CampaignStyleCardPreview: React.FC<{ payload: CampaignPreviewPayload }> = ({
  payload,
}) => {
  if (payload.campaignDesignStyle === "screenshot_bold") {
    return (
      <Box
        sx={{
          borderRadius: 3,
          p: 2,
          color: "white",
          background:
            "linear-gradient(135deg, #0f4da8 0%, #0f6ddf 58%, #23c9db 100%)",
          position: "relative",
          overflow: "hidden",
          minHeight: 202,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            width: 160,
            height: 160,
            borderRadius: "50%",
            right: -40,
            top: -50,
            bgcolor: "rgba(255,255,255,0.15)",
            filter: "blur(4px)",
          }}
        />
        <Stack direction="row" spacing={1.6} justifyContent="space-between">
          <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
            <Chip
              size="small"
              icon={<SparklesIcon sx={{ color: "inherit !important" }} />}
              label="Featured"
              sx={{
                alignSelf: "flex-start",
                bgcolor: "rgba(255,255,255,0.2)",
                color: "white",
                fontWeight: 700,
              }}
            />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, ...textClampSx(2) }}>
              {payload.headline || "Your headline appears here"}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255,255,255,0.92)", ...textClampSx(3) }}
            >
              {payload.subheading || "Your subheading appears here"}
            </Typography>
            <Button
              size="small"
              variant="contained"
              sx={{
                alignSelf: "flex-start",
                bgcolor: "white",
                color: "#0f4da8",
                fontWeight: 700,
                mt: 0.5,
                textTransform: "none",
              }}
            >
              {payload.cta || "Install now"}
            </Button>
          </Stack>
          <PreviewImageTile src={payload.adImageUrl} size={108} />
        </Stack>
      </Box>
    );
  }

  if (payload.campaignDesignStyle === "screenshot_minimal") {
    return (
      <Paper
        variant="outlined"
        sx={{ borderRadius: 3, p: 1.5, bgcolor: "#f8fafc", borderColor: "#e2e8f0" }}
      >
        <Stack spacing={1.2}>
          <Stack direction="row" spacing={0.8} alignItems="center">
            <SparklesIcon sx={{ fontSize: 16, color: "#334155" }} />
            <Typography variant="caption" sx={{ fontWeight: 700, color: "#334155" }}>
              Suggested App
            </Typography>
          </Stack>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, ...textClampSx(2) }}>
            {payload.headline || "Your headline appears here"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={textClampSx(2)}>
            {payload.subheading || "Your subheading appears here"}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <PreviewImageTile src={payload.adImageUrl} size={58} />
            <Button
              size="small"
              variant="outlined"
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              {payload.cta || "Open"}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{ borderRadius: 2.5, p: 1.7, borderColor: "#dbe3ef", boxShadow: "0 4px 14px rgba(15,23,42,0.08)" }}
    >
      <Stack direction="row" spacing={1.6}>
        <PreviewImageTile src={payload.adImageUrl} size={88} />
        <Stack spacing={0.6} sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{ textTransform: "uppercase", letterSpacing: 0.4, color: "#475569", fontWeight: 700 }}
          >
            Recommended
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, ...textClampSx(2) }}>
            {payload.headline || "Your headline appears here"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={textClampSx(2)}>
            {payload.subheading || "Your subheading appears here"}
          </Typography>
          <Button
            size="small"
            variant="contained"
            sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 700 }}
          >
            {payload.cta || "Get app"}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

const DeviceShell: React.FC<{
  platform: PreviewPlatform;
  children: React.ReactNode;
}> = ({ platform, children }) => (
  <Box
    sx={{
      mx: "auto",
      width: 320,
      maxWidth: "100%",
      borderRadius: 6,
      p: 1,
      bgcolor: platform === "ios" ? "#111827" : "#0f172a",
      boxShadow: "0 24px 40px rgba(15,23,42,0.28)",
    }}
  >
    <Box
      sx={{
        height: 20,
        width: 92,
        borderRadius: 2,
        bgcolor: platform === "ios" ? "#1f2937" : "#111827",
        mx: "auto",
        mb: 1,
      }}
    />
    <Box sx={{ borderRadius: 4.5, bgcolor: "#ffffff", p: 1.25 }}>{children}</Box>
  </Box>
);

type CampaignFormState = {
  name: string;
  targetType: "all_apps" | "specific_app";
  appId: string;
  campaignBrief: string;
  defaultLocale: string;
  targetLocales: string[];
  adImageUrl: string;
  campaignDesignStyle: CrossPromotionDesignStyle;
  iosStoreLink: string;
  androidStoreLink: string;
  localizedContent: Record<string, CrossPromotionLocalizedCopy>;
};

const DEFAULT_FORM_STATE: CampaignFormState = {
  name: "",
  targetType: "specific_app",
  appId: "",
  campaignBrief: "",
  defaultLocale: "en",
  targetLocales: [],
  adImageUrl: "",
  campaignDesignStyle: "screenshot_classic",
  iosStoreLink: "",
  androidStoreLink: "",
  localizedContent: {
    en: {
      headline: "",
      subheading: "",
      cta: "",
    },
  },
};

const isHttpsUrl = (value: string): boolean => {
  const safe = String(value || "").trim();
  if (!safe) return false;
  try {
    const parsed = new URL(safe);
    return parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const parseJsonSafely = <T,>(text: string): T | null => {
  const raw = String(text || "").trim();
  if (!raw) return null;

  try {
    return JSON.parse(raw) as T;
  } catch {
    // continue
  }

  const fenced =
    raw.match(/```json\s*([\s\S]*?)\s*```/i)?.[1] ||
    raw.match(/```\s*([\s\S]*?)\s*```/i)?.[1];
  if (fenced) {
    try {
      return JSON.parse(fenced) as T;
    } catch {
      // continue
    }
  }

  const start = raw.indexOf("{");
  const end = raw.lastIndexOf("}");
  if (start >= 0 && end > start) {
    try {
      return JSON.parse(raw.slice(start, end + 1)) as T;
    } catch {
      return null;
    }
  }

  return null;
};

const syncLocalizedContent = (
  current: Record<string, CrossPromotionLocalizedCopy>,
  defaultLocale: string,
  targetLocales: string[],
): Record<string, CrossPromotionLocalizedCopy> => {
  const orderedLocales = [
    defaultLocale,
    ...targetLocales.filter(
      (locale) => locale.trim().toLowerCase() !== defaultLocale.trim().toLowerCase(),
    ),
  ];

  const next = orderedLocales.reduce<Record<string, CrossPromotionLocalizedCopy>>(
    (acc, locale) => {
      const existing = current[locale];
      acc[locale] = {
        headline: existing?.headline || "",
        subheading: existing?.subheading || "",
        cta: existing?.cta || "",
      };
      return acc;
    },
    {},
  );

  return next;
};

const CrossPromotion: React.FC = () => {
  const { user } = useAuth();
  const [apps, setApps] = useState<App[]>([]);
  const [campaigns, setCampaigns] = useState<CrossPromotionCampaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] =
    useState<CrossPromotionCampaign | null>(null);
  const [formState, setFormState] =
    useState<CampaignFormState>(DEFAULT_FORM_STATE);
  const [previewLocale, setPreviewLocale] = useState(DEFAULT_FORM_STATE.defaultLocale);
  const [previewPlatform, setPreviewPlatform] = useState<PreviewPlatform>("ios");
  const [previewMode, setPreviewMode] = useState<"selected" | "all">("selected");
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    intent: true,
    locales: true,
    image: true,
    links: true,
    preview: true,
    copy: true,
  });

  const localeOptions = AVAILABLE_LANGUAGES.map((locale) => ({
    value: locale.code,
    label: `${locale.flag} ${locale.code} - ${locale.name}`,
  }));

  const activeCampaignId = useMemo(
    () => campaigns.find((item) => item.status === "active")?.campaignId,
    [campaigns],
  );
  const activeCampaigns = useMemo(
    () => campaigns.filter((item) => item.status === "active"),
    [campaigns],
  );
  const selectedApp = useMemo(
    () => apps.find((app) => app.appId === formState.appId) || null,
    [apps, formState.appId],
  );

  const selectedTargetLocaleOptions = useMemo(
    () =>
      localeOptions.filter((option) =>
        formState.targetLocales.some(
          (locale) => locale.toLowerCase() === option.value.toLowerCase(),
        ),
      ),
    [formState.targetLocales, localeOptions],
  );

  const orderedLocales = useMemo(() => {
    const targets = formState.targetLocales.filter(
      (locale) => locale.toLowerCase() !== formState.defaultLocale.toLowerCase(),
    );
    return [formState.defaultLocale, ...targets];
  }, [formState.defaultLocale, formState.targetLocales]);

  const previewCopy = useMemo(() => {
    const safeLocale =
      orderedLocales.find((locale) => locale === previewLocale) || orderedLocales[0];
    const selectedCopy = formState.localizedContent[safeLocale] || {
      headline: "",
      subheading: "",
      cta: "",
    };
    return {
      locale: safeLocale,
      ...selectedCopy,
    };
  }, [formState.localizedContent, orderedLocales, previewLocale]);

  const previewPayload = useMemo<CampaignPreviewPayload>(
    () => ({
      headline: previewCopy.headline,
      subheading: previewCopy.subheading,
      cta: previewCopy.cta,
      adImageUrl: formState.adImageUrl,
      campaignDesignStyle: formState.campaignDesignStyle,
      storeLink:
        previewPlatform === "ios" ? formState.iosStoreLink : formState.androidStoreLink,
    }),
    [previewCopy, formState, previewPlatform],
  );

  const resetForm = () => {
    setFormState((prev) => ({
      ...DEFAULT_FORM_STATE,
      appId: apps[0]?.appId || prev.appId || "",
    }));
    setPreviewLocale(DEFAULT_FORM_STATE.defaultLocale);
    setPreviewPlatform("ios");
    setPreviewMode("selected");
    setExpandedSections({
      intent: true,
      locales: true,
      image: true,
      links: true,
      preview: true,
      copy: true,
    });
    setEditingCampaign(null);
  };

  const hydrateFormFromCampaign = (campaign: CrossPromotionCampaign) => {
    const allLocales = Object.keys(campaign.localizedContent || {});
    const targetLocales = allLocales.filter(
      (locale) => locale.toLowerCase() !== campaign.defaultLocale.toLowerCase(),
    );

    setFormState({
      name: campaign.name,
      targetType: campaign.targeting?.type || (campaign.appId ? "specific_app" : "all_apps"),
      appId: campaign.targeting?.appId || campaign.appId || "",
      campaignBrief: campaign.campaignBrief || "",
      defaultLocale: campaign.defaultLocale,
      targetLocales,
      adImageUrl: campaign.adImageUrl,
      campaignDesignStyle: campaign.campaignDesignStyle,
      iosStoreLink: campaign.storeLinks.ios || "",
      androidStoreLink: campaign.storeLinks.android || "",
      localizedContent: syncLocalizedContent(
        campaign.localizedContent || {},
        campaign.defaultLocale,
        targetLocales,
      ),
    });
    setPreviewLocale(campaign.defaultLocale);
    setPreviewPlatform("ios");
    setPreviewMode("selected");
    setExpandedSections({
      intent: true,
      locales: true,
      image: true,
      links: true,
      preview: true,
      copy: true,
    });
  };

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const items = await CrossPromotionService.listCampaigns();
      setCampaigns(items);
    } catch (loadError: any) {
      console.error(loadError);
      setError(loadError?.message || "Failed to load campaigns.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadApps = useCallback(async () => {
    if (!user?.email) return;
    try {
      const appList = await AppService.getUserApps(user.email);
      setApps(appList);
    } catch (loadError: any) {
      console.error(loadError);
      setError(loadError?.message || "Failed to load registered apps.");
    }
  }, [user?.email]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  useEffect(() => {
    loadApps();
  }, [loadApps]);

  useEffect(() => {
    if (!apps.length) return;
    setFormState((prev) => {
      if (prev.appId) return prev;
      const firstApp = apps[0];
      return {
        ...prev,
        appId: firstApp.appId,
        iosStoreLink: String(firstApp.iosStoreUrl || "").trim() || prev.iosStoreLink,
        androidStoreLink:
          String(firstApp.androidStoreUrl || "").trim() || prev.androidStoreLink,
      };
    });
  }, [apps]);

  useEffect(() => {
    if (!orderedLocales.length) return;
    if (!orderedLocales.includes(previewLocale)) {
      setPreviewLocale(orderedLocales[0]);
    }
  }, [orderedLocales, previewLocale]);

  const validateForm = (): string | null => {
    if (!formState.name.trim()) return "Campaign name is required.";
    if (formState.targetType === "specific_app" && !formState.appId.trim()) {
      return "Select a registered app for specific app targeting.";
    }
    if (!formState.campaignBrief.trim()) {
      return "Campaign brief is required.";
    }
    if (!formState.defaultLocale.trim()) return "Base locale is required.";
    if (!isHttpsUrl(formState.adImageUrl)) {
      return "Upload a campaign image before saving.";
    }
    if (!isHttpsUrl(formState.iosStoreLink)) {
      return "iOS store link must be a valid https URL.";
    }
    if (!isHttpsUrl(formState.androidStoreLink)) {
      return "Android store link must be a valid https URL.";
    }

    for (const locale of orderedLocales) {
      const copy = formState.localizedContent[locale];
      if (!copy?.headline?.trim() || !copy?.subheading?.trim() || !copy?.cta?.trim()) {
        return `Localized copy is incomplete for ${locale}.`;
      }
    }

    return null;
  };

  const buildCreateDto = (): CreateCrossPromotionCampaignDTO => {
    const localizedContent = Object.fromEntries(
      orderedLocales.map((locale) => [
        locale,
        {
          headline: String(formState.localizedContent[locale]?.headline || "").trim(),
          subheading: String(formState.localizedContent[locale]?.subheading || "").trim(),
          cta: String(formState.localizedContent[locale]?.cta || "").trim(),
        },
      ]),
    );

    return {
      name: formState.name.trim(),
      targeting:
        formState.targetType === "specific_app"
          ? {
              type: "specific_app",
              appId: formState.appId.trim(),
              appName: selectedApp?.name || "Unknown App",
            }
          : {
              type: "all_apps",
            },
      appId: formState.targetType === "specific_app" ? formState.appId.trim() : "",
      appName:
        formState.targetType === "specific_app" ? selectedApp?.name || "Unknown App" : "",
      campaignBrief: formState.campaignBrief.trim(),
      status: "draft",
      defaultLocale: formState.defaultLocale.trim(),
      localizedContent,
      adImageUrl: formState.adImageUrl.trim(),
      campaignDesignStyle: formState.campaignDesignStyle,
      storeLinks: {
        ios: formState.iosStoreLink.trim(),
        android: formState.androidStoreLink.trim(),
      },
    };
  };

  const updateCopyField = (
    locale: string,
    key: keyof CrossPromotionLocalizedCopy,
    value: string,
  ) => {
    setFormState((prev) => ({
      ...prev,
      localizedContent: {
        ...prev.localizedContent,
        [locale]: {
          ...(prev.localizedContent[locale] || {
            headline: "",
            subheading: "",
            cta: "",
          }),
          [key]: value,
        },
      },
    }));
  };

  const handleDefaultLocaleChange = (locale: string) => {
    setFormState((prev) => {
      const nextTargetLocales = prev.targetLocales.filter(
        (item) => item.toLowerCase() !== locale.toLowerCase(),
      );
      return {
        ...prev,
        defaultLocale: locale,
        targetLocales: nextTargetLocales,
        localizedContent: syncLocalizedContent(
          prev.localizedContent,
          locale,
          nextTargetLocales,
        ),
      };
    });
  };

  const handleTargetLocalesChange = (locales: string[]) => {
    setFormState((prev) => ({
      ...prev,
      targetLocales: locales,
      localizedContent: syncLocalizedContent(
        prev.localizedContent,
        prev.defaultLocale,
        locales,
      ),
    }));
  };

  const handleAppSelection = (appId: string) => {
    setFormState((prev) => {
      const selected = apps.find((app) => app.appId === appId);
      if (!selected) {
        return { ...prev, appId };
      }

      const iosStoreUrl = String(selected.iosStoreUrl || "").trim();
      const androidStoreUrl = String(selected.androidStoreUrl || "").trim();

      return {
        ...prev,
        appId,
        iosStoreLink: iosStoreUrl || prev.iosStoreLink,
        androidStoreLink: androidStoreUrl || prev.androidStoreLink,
      };
    });
  };

  const handleTargetTypeChange = (targetType: "all_apps" | "specific_app") => {
    if (targetType === "all_apps") {
      setFormState((prev) => ({ ...prev, targetType, appId: "" }));
      return;
    }

    const fallbackAppId = formState.appId || apps[0]?.appId || "";
    setFormState((prev) => {
      const selected = apps.find((app) => app.appId === fallbackAppId);
      const iosStoreUrl = String(selected?.iosStoreUrl || "").trim();
      const androidStoreUrl = String(selected?.androidStoreUrl || "").trim();

      return {
        ...prev,
        targetType,
        appId: fallbackAppId,
        iosStoreLink: iosStoreUrl || prev.iosStoreLink,
        androidStoreLink: androidStoreUrl || prev.androidStoreLink,
      };
    });
  };

  const handleToggleSection =
    (section: "intent" | "locales" | "image" | "links" | "preview" | "copy") =>
    (_event: React.SyntheticEvent, expanded: boolean) => {
      setExpandedSections((prev) => ({ ...prev, [section]: expanded }));
    };

  const handleGenerateWithAI = async () => {
    if (!formState.campaignBrief.trim()) {
      setError("Add what the campaign is about before generating.");
      return;
    }

    setAiGenerating(true);
    setError(null);
    try {
      const locales = orderedLocales;
      const prompt = [
        "You are a mobile growth copywriter and localization specialist.",
        "Generate a cross-promotion campaign from the brief.",
        "Output JSON only.",
        "",
        `Campaign brief: ${formState.campaignBrief.trim()}`,
        `Base locale: ${formState.defaultLocale}`,
        `Target locales: ${formState.targetLocales.join(", ") || "none"}`,
        `Design style: ${formState.campaignDesignStyle}`,
        "",
        "Requirements:",
        "- Generate a concise campaignName.",
        "- Generate localized copy for every required locale.",
        "- Keep headline <= 70 chars, subheading <= 140 chars, CTA <= 28 chars.",
        "- Do not include markdown.",
        "",
        "Return this exact JSON shape:",
        JSON.stringify(
          {
            campaignName: "string",
            localizedEntries: locales.map((locale) => ({
              locale,
              headline: "string",
              subheading: "string",
              cta: "string",
            })),
          },
          null,
          2,
        ),
      ].join("\n");

      const aiResult = await AIService.generateText(prompt, {
        model: AIService.getDefaultModel(),
        temperature: 0.4,
        maxTokens: 2048,
      });

      const parsed = parseJsonSafely<{
        campaignName?: string;
        localizedEntries?: Array<{
          locale?: string;
          headline?: string;
          subheading?: string;
          cta?: string;
        }>;
      }>(aiResult.text);

      if (!parsed) {
        throw new Error("AI returned an invalid response. Please retry.");
      }

      const generatedMap = Object.fromEntries(
        (parsed.localizedEntries || [])
          .map((entry) => {
            const locale = String(entry?.locale || "").trim();
            if (!locale) return null;
            return [
              locale,
              {
                headline: String(entry?.headline || "").trim(),
                subheading: String(entry?.subheading || "").trim(),
                cta: String(entry?.cta || "").trim(),
              },
            ] as const;
          })
          .filter((item): item is readonly [string, CrossPromotionLocalizedCopy] =>
            Boolean(item),
          ),
      );

      const merged = syncLocalizedContent(
        {
          ...formState.localizedContent,
          ...generatedMap,
        },
        formState.defaultLocale,
        formState.targetLocales,
      );

      setFormState((prev) => ({
        ...prev,
        name: String(parsed.campaignName || prev.name).trim() || prev.name,
        localizedContent: merged,
      }));
      setSuccess("Campaign copy generated and localized.");
    } catch (generateError: any) {
      console.error(generateError);
      setError(generateError?.message || "Failed to generate campaign with AI.");
    } finally {
      setAiGenerating(false);
    }
  };

  const handleUploadImage = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    setError(null);
    try {
      const response = await CrossPromotionService.uploadCampaignImage(
        file,
        editingCampaign?.campaignId,
      );
      setFormState((prev) => ({
        ...prev,
        adImageUrl: response.asset.downloadUrl,
      }));
      setSuccess("Image uploaded and optimized successfully.");
    } catch (uploadError: any) {
      console.error(uploadError);
      setError(uploadError?.message || "Failed to upload campaign image.");
    } finally {
      setUploadingImage(false);
      event.target.value = "";
    }
  };

  const handleOpenCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleOpenEdit = (campaign: CrossPromotionCampaign) => {
    setEditingCampaign(campaign);
    hydrateFormFromCampaign(campaign);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!user?.email) {
      setError("Authentication is required.");
      return;
    }

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const dto = buildCreateDto();
      if (editingCampaign) {
        await CrossPromotionService.updateCampaign(
          editingCampaign.campaignId,
          user.email,
          dto,
        );
        setSuccess("Campaign updated.");
      } else {
        await CrossPromotionService.createCampaign(user.email, dto);
        setSuccess("Campaign created.");
      }

      setDialogOpen(false);
      resetForm();
      await loadCampaigns();
    } catch (saveError: any) {
      console.error(saveError);
      setError(saveError?.message || "Failed to save campaign.");
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (campaignId: string) => {
    if (!user?.email) return;
    try {
      setError(null);
      await CrossPromotionService.activateCampaign(campaignId, user.email);
      setSuccess("Campaign activated.");
      await loadCampaigns();
    } catch (activateError: any) {
      console.error(activateError);
      setError(activateError?.message || "Failed to activate campaign.");
    }
  };

  const handleDeactivate = async (campaignId: string) => {
    if (!user?.email) return;
    try {
      setError(null);
      await CrossPromotionService.deactivateCampaign(campaignId, user.email);
      setSuccess("Campaign archived.");
      await loadCampaigns();
    } catch (deactivateError: any) {
      console.error(deactivateError);
      setError(deactivateError?.message || "Failed to archive campaign.");
    }
  };

  const handleDelete = async (campaignId: string) => {
    if (!window.confirm("Delete this cross promotion campaign?")) return;
    try {
      setError(null);
      await CrossPromotionService.deleteCampaign(campaignId);
      setSuccess("Campaign deleted.");
      await loadCampaigns();
    } catch (deleteError: any) {
      console.error(deleteError);
      setError(deleteError?.message || "Failed to delete campaign.");
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
        Cross Promotion
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Build one high-conversion campaign quickly with AI generation, locale selection, and optimized image upload.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Active Campaigns
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {activeCampaigns.length} currently active
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {activeCampaigns.length === 0 ? (
              <Chip size="small" label="No active campaign" />
            ) : (
              activeCampaigns.map((campaign) => (
                <Chip
                  key={campaign.campaignId}
                  size="small"
                  color="success"
                  label={
                    campaign.targeting?.type === "all_apps"
                      ? `${campaign.name} (All Apps)`
                      : `${campaign.name} (${campaign.targeting?.appName || campaign.appName || "Unknown App"})`
                  }
                />
              ))
            )}
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 2.5, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Campaigns
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
            New Campaign
          </Button>
        </Stack>
      </Paper>

      {loading ? (
        <Typography color="text.secondary">Loading campaigns...</Typography>
      ) : campaigns.length === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography color="text.secondary">No campaigns yet. Create your first campaign.</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {campaigns.map((campaign) => {
            const localesCount = Object.keys(campaign.localizedContent || {}).length;
            const isActive = campaign.status === "active";
            return (
              <Grid item xs={12} md={6} key={campaign.campaignId}>
                <Card variant="outlined" sx={{ height: "100%" }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {campaign.name}
                      </Typography>
                      <Chip
                        label={campaign.status}
                        color={isActive ? "success" : campaign.status === "draft" ? "default" : "warning"}
                        size="small"
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Targeting:{" "}
                      {campaign.targeting?.type === "all_apps"
                        ? "All registered apps"
                        : campaign.targeting?.appName || campaign.appName || "Specific app"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Base locale: {campaign.defaultLocale}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Locales: {localesCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Style: {campaign.campaignDesignStyle}
                    </Typography>
                    {campaign.adImageUrl ? (
                      <Box
                        component="img"
                        src={campaign.adImageUrl}
                        alt={campaign.name}
                        sx={{
                          width: "100%",
                          borderRadius: 1.5,
                          border: "1px solid",
                          borderColor: "divider",
                          maxHeight: 200,
                          objectFit: "cover",
                        }}
                      />
                    ) : null}
                    {activeCampaignId === campaign.campaignId && (
                      <Typography variant="caption" color="success.main" sx={{ fontWeight: 600, mt: 1, display: "block" }}>
                        Live campaign serving endpoint traffic
                      </Typography>
                    )}
                  </CardContent>
                  <CardActions sx={{ justifyContent: "space-between", px: 2, pb: 2 }}>
                    <Stack direction="row" spacing={1}>
                      <IconButton onClick={() => handleOpenEdit(campaign)} aria-label="Edit campaign">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(campaign.campaignId)} aria-label="Delete campaign">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    {isActive ? (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DeactivateIcon />}
                        onClick={() => handleDeactivate(campaign.campaignId)}
                      >
                        Archive
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<ActivateIcon />}
                        onClick={() => handleActivate(campaign.campaignId)}
                      >
                        Activate
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>{editingCampaign ? "Edit Campaign" : "Create Campaign"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            <Accordion
              expanded={expandedSections.intent}
              onChange={handleToggleSection("intent")}
              disableGutters
              elevation={0}
              sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  1. Campaign Intent
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      fullWidth
                      label="Targeting"
                      value={formState.targetType}
                      onChange={(event) =>
                        handleTargetTypeChange(
                          event.target.value as "all_apps" | "specific_app",
                        )
                      }
                    >
                      <MenuItem value="specific_app">Specific app</MenuItem>
                      <MenuItem value="all_apps">All registered apps</MenuItem>
                    </TextField>
                  </Grid>
                  {formState.targetType === "specific_app" ? (
                    <Grid item xs={12} md={8}>
                      <TextField
                        select
                        fullWidth
                        label="Registered app"
                        value={formState.appId}
                        onChange={(event) => handleAppSelection(event.target.value)}
                        helperText={
                          apps.length
                            ? "Campaign will only be served to this app. App store URLs auto-fill when available."
                            : "No registered apps found. Create one in App Registration first."
                        }
                      >
                        {apps.map((app) => (
                          <MenuItem key={app.appId} value={app.appId}>
                            {app.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  ) : (
                    <Grid item xs={12} md={8}>
                      <Alert severity="info">This campaign can be served to any connected app.</Alert>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <TextField
                      label="What is this campaign about?"
                      placeholder="Example: Promote our voice translator app to frequent travelers who need offline translation."
                      fullWidth
                      multiline
                      minRows={3}
                      value={formState.campaignBrief}
                      onChange={(event) =>
                        setFormState((prev) => ({ ...prev, campaignBrief: event.target.value }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Campaign name"
                      fullWidth
                      value={formState.name}
                      onChange={(event) =>
                        setFormState((prev) => ({ ...prev, name: event.target.value }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      label="Campaign design style"
                      fullWidth
                      value={formState.campaignDesignStyle}
                      onChange={(event) =>
                        setFormState((prev) => ({
                          ...prev,
                          campaignDesignStyle: event.target.value as CrossPromotionDesignStyle,
                        }))
                      }
                    >
                      {DESIGN_STYLE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedSections.locales}
              onChange={handleToggleSection("locales")}
              disableGutters
              elevation={0}
              sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  2. Locales & AI Copy Generation
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      fullWidth
                      label="Base locale"
                      value={formState.defaultLocale}
                      onChange={(event) => handleDefaultLocaleChange(event.target.value)}
                    >
                      {localeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Autocomplete
                      multiple
                      options={localeOptions.filter(
                        (option) =>
                          option.value.toLowerCase() !==
                          formState.defaultLocale.toLowerCase(),
                      )}
                      value={selectedTargetLocaleOptions}
                      onChange={(_, value) =>
                        handleTargetLocalesChange(value.map((item) => item.value))
                      }
                      getOptionLabel={(option) => option.label}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Target locales"
                          placeholder="Select locales to auto-translate"
                        />
                      )}
                    />
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ mt: 1 }}
                      alignItems="center"
                    >
                      <Button
                        size="small"
                        variant="text"
                        onClick={() =>
                          handleTargetLocalesChange(
                            localeOptions
                              .map((option) => option.value)
                              .filter(
                                (locale) =>
                                  locale.toLowerCase() !==
                                  formState.defaultLocale.toLowerCase(),
                              ),
                          )
                        }
                      >
                        Select all locales
                      </Button>
                    </Stack>
                  </Grid>
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                      <Button
                        variant="contained"
                        startIcon={aiGenerating ? <CircularProgress size={16} /> : <AIIcon />}
                        disabled={aiGenerating}
                        onClick={handleGenerateWithAI}
                      >
                        {aiGenerating ? "Generating..." : "Generate With AI"}
                      </Button>
                      <Typography variant="body2" color="text.secondary">
                        AI generates base-locale copy and translates it to selected locales.
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedSections.image}
              onChange={handleToggleSection("image")}
              disableGutters
              elevation={0}
              sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  3. Campaign Image (Optimized Upload)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={uploadingImage ? <CircularProgress size={16} /> : <UploadIcon />}
                      disabled={uploadingImage}
                    >
                      {uploadingImage ? "Uploading..." : "Upload Image"}
                      <input
                        hidden
                        accept="image/*"
                        type="file"
                        onChange={handleUploadImage}
                      />
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      Image is compressed with Sharp on backend for faster mobile fetch.
                    </Typography>
                  </Stack>
                  {formState.adImageUrl ? (
                    <Box
                      component="img"
                      src={formState.adImageUrl}
                      alt="Campaign preview"
                      sx={{
                        width: "100%",
                        maxHeight: 280,
                        objectFit: "cover",
                        borderRadius: 1.5,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    />
                  ) : null}
                </Stack>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedSections.links}
              onChange={handleToggleSection("links")}
              disableGutters
              elevation={0}
              sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  4. Store Links
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="iOS store link"
                      fullWidth
                      value={formState.iosStoreLink}
                      onChange={(event) =>
                        setFormState((prev) => ({ ...prev, iosStoreLink: event.target.value }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Android store link"
                      fullWidth
                      value={formState.androidStoreLink}
                      onChange={(event) =>
                        setFormState((prev) => ({ ...prev, androidStoreLink: event.target.value }))
                      }
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedSections.preview}
              onChange={handleToggleSection("preview")}
              disableGutters
              elevation={0}
              sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  5. Live Preview
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1.2}
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", md: "center" }}
                  >
                    <Stack direction="row" spacing={1.2} alignItems="center" flexWrap="wrap">
                      <TextField
                        select
                        size="small"
                        label="Preview locale"
                        value={previewCopy.locale}
                        onChange={(event) => setPreviewLocale(event.target.value)}
                        sx={{ minWidth: 160 }}
                      >
                        {orderedLocales.map((locale) => (
                          <MenuItem key={locale} value={locale}>
                            {locale}
                          </MenuItem>
                        ))}
                      </TextField>
                      <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={previewPlatform}
                        onChange={(_, value: PreviewPlatform | null) =>
                          value ? setPreviewPlatform(value) : null
                        }
                      >
                        <ToggleButton value="ios">
                          <IOSIcon sx={{ mr: 0.7, fontSize: 16 }} />
                          iOS
                        </ToggleButton>
                        <ToggleButton value="android">
                          <AndroidIcon sx={{ mr: 0.7, fontSize: 16 }} />
                          Android
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Stack>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={previewMode}
                      onChange={(_, value: "selected" | "all" | null) =>
                        value ? setPreviewMode(value) : null
                      }
                    >
                      <ToggleButton value="selected">Selected style</ToggleButton>
                      <ToggleButton value="all">Compare all styles</ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>

                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Tooltip title={`Recommended max ${COPY_LIMITS.headline} characters`}>
                      <Chip
                        size="small"
                        color={
                          (previewCopy.headline || "").length <= COPY_LIMITS.headline
                            ? "success"
                            : "warning"
                        }
                        label={`Headline ${(previewCopy.headline || "").length}/${COPY_LIMITS.headline}`}
                      />
                    </Tooltip>
                    <Tooltip title={`Recommended max ${COPY_LIMITS.subheading} characters`}>
                      <Chip
                        size="small"
                        color={
                          (previewCopy.subheading || "").length <= COPY_LIMITS.subheading
                            ? "success"
                            : "warning"
                        }
                        label={`Subheading ${(previewCopy.subheading || "").length}/${COPY_LIMITS.subheading}`}
                      />
                    </Tooltip>
                    <Tooltip title={`Recommended max ${COPY_LIMITS.cta} characters`}>
                      <Chip
                        size="small"
                        color={
                          (previewCopy.cta || "").length <= COPY_LIMITS.cta
                            ? "success"
                            : "warning"
                        }
                        label={`CTA ${(previewCopy.cta || "").length}/${COPY_LIMITS.cta}`}
                      />
                    </Tooltip>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        previewPayload.storeLink?.trim()
                          ? `${previewPlatform === "ios" ? "iOS" : "Android"} store link set`
                          : `${previewPlatform === "ios" ? "iOS" : "Android"} store link missing`
                      }
                    />
                  </Stack>

                  {previewMode === "selected" ? (
                    <DeviceShell platform={previewPlatform}>
                      <CampaignStyleCardPreview payload={previewPayload} />
                    </DeviceShell>
                  ) : (
                    <Grid container spacing={2}>
                      {DESIGN_STYLE_OPTIONS.map((styleOption) => (
                        <Grid item xs={12} md={4} key={styleOption.value}>
                          <Stack spacing={1}>
                            <Typography variant="caption" color="text.secondary">
                              {styleOption.label}
                            </Typography>
                            <DeviceShell platform={previewPlatform}>
                              <CampaignStyleCardPreview
                                payload={{
                                  ...previewPayload,
                                  campaignDesignStyle: styleOption.value,
                                }}
                              />
                            </DeviceShell>
                          </Stack>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedSections.copy}
              onChange={handleToggleSection("copy")}
              disableGutters
              elevation={0}
              sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  6. Localized Copy Review
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {orderedLocales.map((locale, index) => (
                    <Box key={locale}>
                      {index > 0 && <Divider sx={{ mb: 2 }} />}
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <Chip label={locale} size="small" color={locale === formState.defaultLocale ? "primary" : "default"} />
                        {locale === formState.defaultLocale ? (
                          <Typography variant="caption" color="text.secondary">Base locale</Typography>
                        ) : null}
                      </Stack>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Headline"
                            value={formState.localizedContent[locale]?.headline || ""}
                            onChange={(event) => updateCopyField(locale, "headline", event.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={5}>
                          <TextField
                            fullWidth
                            label="Subheading"
                            value={formState.localizedContent[locale]?.subheading || ""}
                            onChange={(event) => updateCopyField(locale, "subheading", event.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label="CTA"
                            value={formState.localizedContent[locale]?.cta || ""}
                            onChange={(event) => updateCopyField(locale, "cta", event.target.value)}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDialogOpen(false);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || aiGenerating || uploadingImage}>
            {saving ? "Saving..." : editingCampaign ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CrossPromotion;
